// =========================================================================
// Devices & keys — step 1 of the operator's flow (17 Oct 2026).
//
// Two things, side by side, because they are two different facts:
//
//   the DEVICE   the address of the operator's registered Ledger — the one
//                key the desk takes a signature from (custody_signers);
//   the KEY      a Ledger account's PUBLIC key and chain code, registered
//                once by cable, from which the bot issues one deposit address
//                per deal (wallet_keys, scheme bip44-tron).
//
// Registering the key is the only write here. The Ledger exports the key
// (cosign/src/ledger.ts exportAccountKey — the TRON app answers the account's
// key with its chain code, nothing shown), then shows the account's FIRST
// address on its own screen for the operator to compare with this one; the
// server derives index 0 from the key itself and refuses a mismatch. Nothing
// exported can spend.
//
// One account per class (slice E, 18 Oct 2026): account 1 issues the deposit
// addresses, accounts 2–4 are where Hot, Merchant Hot and Treasury renew
// from (lib/custody-renewals KEY_ACCOUNT_CLASS). The picker says which is
// which, and the server refuses a second active key on an account.
//
// On the design system (22 Oct 2026): one line per key, one per state; the
// how-to is behind a tap.
// =========================================================================

import { SigninQuiet } from '../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../lib/errors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Usb, ShieldCheck } from 'lucide-react';
import { custody as api, type CustodyKey, type WalletBalance } from '../../lib/api';
import { replacementPlan, HOLDINGS_CURRENT_MS, REPLACEMENT_ORDER } from '../../lib/custody-keys-replace';
import { useHoldingsForPlan } from '../../lib/useHoldingsForPlan';
import { ApiError } from '../../lib/errors';
import { fmtDate } from '../../lib/format';
import { CLASS_WORD } from '../../lib/custody-format';
import { Card, StateLine, Pending, Address, Pill, Value, MoreDetails } from '../../components/kit';
import { KEY_ACCOUNTS, KEY_ACCOUNT_CLASS, accountLabel, accountWord } from '../../lib/custody-renewals';
import { webHid, exportAccountKey, signOnLedger, buildNetwork, type LedgerStep } from '../../lib/ledger-cable';
import { buildKeyProof } from '../../../../signer/src/key-approval.mjs';
import { rememberAccountKey } from '../../lib/custody-keys-local';
import { keyRefusal } from './refusals';
import { FIELD, SELECT } from './field';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);
const remedyOf = (err: unknown): string | null => {
  const remedy = (err as { remedy?: unknown } | null)?.remedy;
  return typeof remedy === 'string' && remedy ? remedy : null;
};

/** What an account's key is for, said: "Hot", "Treasury" — "customer deposits" for account 1, null for an account with no job. */
function jobOf(account: number | null): string | null {
  const cls = account === null ? undefined : KEY_ACCOUNT_CLASS[account];
  if (cls === undefined) return null;
  if (cls === 'deposit') return 'customer deposits';
  return CLASS_WORD[cls] ?? cls;
}

/**
 * Why the key was not registered: one line, and what to do behind it. The
 * two reasons slice E added are said here, over refusals.ts's older ones.
 */
function keyRefusalWords(err: unknown, account: number): { line: string; more: string } {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  const job = jobOf(account);
  switch (reason) {
    case 'account_taken':
      return { line: `Account ${account + 1} already has an active key.`, more: `${job ? `Account ${account + 1} is ${job}'s. ` : ''}Nothing was changed. To use a different key for it, ask the desk to retire the old one first.` };
    case 'already_registered':
      return { line: `Account ${account + 1} is registered already — pick another.`, more: 'This key is on file. Nothing was changed; the accounts without a key are the ones to register.' };
    case 'no_registered_device':
      return { line: 'No signing device registered for you.', more: 'No key can be registered against it. Ask for your Ledger\'s address to be registered first, then try again.' };
    case 'key_not_this_ledger':
      return { line: 'That key is from another Ledger.', more: `Nothing was registered. Plug in the Ledger registered to you and export account ${account + 1} from it.` };
    default:
      return { line: 'Not registered — try again.', more: keyRefusal(err) };
  }
}

/** The class word for a registered key: the server's `classWord` when it sends one, else the convention's for its account. */
function keyWord(k: CustodyKey): string | null {
  if (typeof k.classWord === 'string' && k.classWord) return k.classWord;
  const cls = k.account === null ? undefined : KEY_ACCOUNT_CLASS[k.account];
  return cls ? CLASS_WORD[cls] ?? cls : null;
}

export function KeyRow({ k, floats, onRetired, confirming, setConfirming }: { k: CustodyKey; floats: ReadonlyArray<WalletBalance> | null; onRetired: () => void; confirming: boolean; setConfirming: (open: boolean) => void }) {
  const active = k.status === 'active';
  const word = keyWord(k);
  // Retire (audit item 17): the key handover when a new Ledger arrives. Never deleted; the account is free again.
  const retire = useMutation({
    mutationFn: () => api.retireKey(k.id),
    onSuccess: onRetired,
  });
  // The confirm is in the page, one line with the procedure for THIS account behind Details (Screens' keys round 3,
  // 23 Oct 2026): where its money goes before the old Ledger is handed over, and what it holds by the readings.
  // The confirmation is held by the screen: one at a time, and never beside the Register form (Astra round c, minor).
  const name = k.label ?? (k.account === null ? 'this key' : `account ${k.account + 1}`);
  // What THIS key still signs: every address it issued, however old, read when the confirm opens (Astra round c, B5).
  // Not in, failed, or any address not currently read → the confirm says so; never "empty".
  // Read on opening and again while open; spoken from only while KNOWN — not after a failed refresh, not once old (round d, #4).
  const holdings = useQuery({ queryKey: ['custody', 'key-holdings', k.id], queryFn: () => api.keyHoldings(k.id), enabled: confirming, staleTime: 0, refetchOnMount: 'always', refetchInterval: confirming ? HOLDINGS_CURRENT_MS / 2 : false, retry: false });
  // Re-judged the moment the answer goes old, and a paused refresh is unknown (round e #4; lib/useHoldingsForPlan).
  const plan = replacementPlan(k.account, floats, useHoldingsForPlan(holdings, confirming));
  // Keys round 5 (minor): the confirm takes the focus when it opens, Escape closes it, and the focus goes back to Retire.
  const confirmRef = useRef<HTMLDivElement>(null);
  const retireRef = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
    else if (wasConfirming.current) retireRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);
  return (
    <div>
    <div className="row items-start">
      <span className="min-w-0 text-body">
        <Value strong>{k.label ?? (k.account === null ? 'Account key' : `Account ${k.account + 1}`)}</Value>
        {word && <Pill className="ml-1.5">{word}</Pill>}
        <span className="ml-1.5 text-meta text-tg-hint">{k.chain}{k.account !== null && k.label ? ` · ${accountWord(k.account)}` : ''}</span>
        <span className="mt-0.5 block text-meta text-tg-hint">
          {k.firstAddress ? <>first address <Address value={k.firstAddress} /></> : 'first address not derivable'}
        </span>
      </span>
      <span className="text-right text-body">
        <span className={active ? 'text-pos' : 'text-tg-hint'}>{active ? 'Active' : k.status}</span>
        <span className="block text-meta text-tg-hint">{k.lastIndex < 0 ? 'no address issued' : `${k.lastIndex + 1} issued`}</span>
        {k.registeredAt && <span className="block text-meta text-tg-hint">registered {fmtDate(k.registeredAt)}</span>}
        {active && !confirming && (
          <button ref={retireRef} type="button" className="btn-secondary mt-1 min-h-[44px] text-body lg:min-h-[40px]" disabled={retire.isPending} onClick={() => setConfirming(true)}>
            {retire.isPending ? 'Retiring…' : 'Retire'}
          </button>
        )}
        {retire.isError && <StateLine tone="bad" className="mt-1" more={keyRefusal(retire.error)}>Not retired — try again.</StateLine>}
      </span>
    </div>
    {active && confirming && (
      <div
        ref={confirmRef}
        tabIndex={-1}
        role="group"
        aria-label={`Retire ${name}`}
        className="mt-2 rounded-md border border-stroke p-3 outline-none focus-visible:ring-2 focus-visible:ring-tg-accent"
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setConfirming(false); } }}
      >
        <StateLine tone="warn" more={<ul className="list-disc space-y-1 pl-4">{plan.lines.map((l) => <li key={l}>{l}</li>)}</ul>}>{plan.headline}</StateLine>
        {holdings.isError && isCustodySignin(holdings.error) && <SigninQuiet error={holdings.error} className="mt-1" />}
        {holdings.isError && !isCustodySignin(holdings.error) && (
          <StateLine tone="bad" className="mt-1" more={textOf(holdings.error)} action={<button type="button" className="btn-link min-h-[44px] text-body" onClick={() => void holdings.refetch()} disabled={holdings.isFetching}>{holdings.isFetching ? 'Reading…' : 'Try again'}</button>}>
            Couldn't read what this key holds.
          </StateLine>
        )}
        <div className="mt-2 flex gap-2">
          <button type="button" className="btn-primary min-h-[44px] text-body lg:min-h-[40px]" disabled={retire.isPending} onClick={() => { retire.mutate(); setConfirming(false); }}>
            {retire.isPending ? 'Retiring…' : `Retire ${name}${word ? ` (${word})` : ''}`}
          </button>
          <button type="button" className="btn-secondary min-h-[44px] text-body lg:min-h-[40px]" onClick={() => setConfirming(false)}>Keep</button>
        </div>
      </div>
    )}
    </div>
  );
}

type Stage =
  | { at: 'idle' }
  | { at: 'exporting'; step: LedgerStep | null; text: string }
  | { at: 'exported'; firstAddress: string; path: string }
  /** The device is asked to sign the proof: the exported address and the proof's facts stay on screen through every step. */
  | { at: 'proving'; firstAddress: string; path: string; device: string; text: string }
  | { at: 'registered'; firstAddress: string; path: string }
  | { at: 'failed'; line: string; more: string; remedy: string | null };

function RegisterKey({ onRegistered, registered, device }: { onRegistered: () => void; registered: CustodyKey[]; /** The operator's registered Ledger address: the proof leaves it. */ device: string | null }) {
  // The first account with no active key is what he came to register (the
  // operator, 22 Oct 2026: with account 1 pre-selected he re-registered the
  // deposit key and was told "not registered"); every account taken, the first.
  const [account, setAccount] = useState<number>(() => KEY_ACCOUNTS.find((a) => !registered.some((k) => k.account === a && k.status === 'active')) ?? KEY_ACCOUNTS[0] ?? 0);
  const [label, setLabel] = useState('');
  const [stage, setStage] = useState<Stage>({ at: 'idle' });
  const network = useMemo(() => buildNetwork(), []);
  const cable = webHid();

  const register = useMutation({
    mutationFn: async () => {
      if (!cable) throw new Error('This browser cannot reach a Ledger over a cable.');
      if (!network) throw new Error('This build of the app names no TRON network. Report this.');
      setStage({ at: 'exporting', step: null, text: 'Starting…' });
      const key = await exportAccountKey({
        hid: cable, account, network,
        onStep: (step, text) => setStage({ at: 'exporting', step, text }),
      });
      setStage({ at: 'exported', firstAddress: key.firstAddress, path: key.path });
      // Accounts 1–3: the registered device vouches for the key by signing a
      // 0 TRX transfer it SHOWS in full — from itself to the first address he
      // just compared — expired before it was made and on no block, so the
      // chain can never take it (the operator, 22 Oct 2026: no blind signing,
      // no message signing). The server accepts the key only from that
      // signature (audit of 21 Oct 2026, D2). Account 0 proves itself: its
      // first address is the device.
      let proof: { rawDataHex: string; signature: string } | undefined;
      if (key.account !== 0) {
        if (!device) throw Object.assign(new Error('No signing device is registered for you.'), { remedy: 'Ask for your Ledger address to be registered first; the proof leaves it.' });
        // The desk's challenge binds the proof to THIS registration — this operator, this account, this key
        // material, this moment; the device shows nothing of it (it sits where a block hash would).
        const at = Date.now();
        const { challenge } = await api.keyProofChallenge({ account: key.account, publicKey: key.publicKey, chainCode: key.chainCode, at });
        const built = buildKeyProof({ deviceAddress: device, firstAddress: key.firstAddress, at, challenge });
        const signed = await signOnLedger({
          hid: cable, network, signer: device, rawDataHex: built.raw_data_hex, derivation: '0/0', account: 0, prove: null,
          onStep: (_step, text) => setStage({ at: 'proving', firstAddress: key.firstAddress, path: key.path, device, text }),
        });
        proof = { rawDataHex: built.raw_data_hex, signature: signed.signature };
      }
      let answer: Awaited<ReturnType<typeof api.registerKey>>;
      try {
        answer = await api.registerKey({
          chain: 'TRC20', account: key.account, publicKey: key.publicKey, chainCode: key.chainCode,
          parentFingerprint: key.parentFingerprint,
          firstAddress: key.firstAddress, label: label.trim() || undefined,
          ...(proof ? { proof } : {}),
        });
      } catch (err) {
        // The account's key is registered already — and it is THIS key, by
        // its first address ('already_registered' for the same key; for
        // accounts 1–3 'account_taken' names the account): a browser that
        // has never held it fills its cache from the export it just made
        // (Astra, 22 Oct 2026), else every address from that key stayed
        // "unchecked" here for ever.
        const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
        const mine = registered.find((k) => k.account === key.account && k.status === 'active');
        if ((reason !== 'account_taken' && reason !== 'already_registered') || !mine || mine.firstAddress !== key.firstAddress) throw err;
        answer = { id: mine.id, account: key.account, firstAddress: key.firstAddress, lastIndex: mine.lastIndex, adopted: [], class: mine.class, classWord: mine.classWord };
      }
      // The public parts stay on this browser, so every address the screens
      // show from this key is derived here again and held against the
      // server's word (lib/custody-keys-local.ts, audit of 21 Oct 2026, E2).
      rememberAccountKey(network, { account: key.account, publicKey: key.publicKey, chainCode: key.chainCode, parentFingerprint: key.parentFingerprint, firstAddress: key.firstAddress });
      // Said explicitly: the proof's own steps set the stage back to
      // 'exporting' on the way, and success alone refreshed the list while the
      // card still read as a Ledger instruction (Screens' Astra review, 22 Oct 2026).
      setStage({ at: 'registered', firstAddress: key.firstAddress, path: key.path });
      return answer;
    },
    onSuccess: () => onRegistered(),
    onError: (err) => { const w = keyRefusalWords(err, account); setStage({ at: 'failed', line: w.line, more: w.more, remedy: remedyOf(err) }); },
  });
  // What the registered key is for, once the server has said: its word, or the convention's — null for the deposit key.
  const registeredFor = register.data?.classWord ?? (KEY_ACCOUNT_CLASS[account] === 'deposit' ? null : jobOf(account));

  if (!cable) {
    return (
      <StateLine tone="warn" more="Registering a key needs the Ledger on a cable, in a browser that can reach it: Brave or Edge on the computer it is plugged into. This browser cannot.">
        This browser cannot reach a Ledger by cable.
      </StateLine>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-meta text-tg-hint">Ledger account</span>
          <select className={`${SELECT} w-full`} value={account} onChange={(e) => setAccount(Number(e.target.value))} disabled={register.isPending}>
            {KEY_ACCOUNTS.map((a) => <option key={a} value={a}>{accountLabel(a)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-meta text-tg-hint">Label (optional)</span>
          <input className={`${FIELD} w-full`} value={label} placeholder="desk Nano" onChange={(e) => setLabel(e.target.value)} disabled={register.isPending} />
        </label>
      </div>

      {/* words:ok — the device's own step, as ledger-cable words it */}
      {stage.at === 'exporting' && <StateLine>{stage.text}</StateLine>}
      {stage.at === 'proving' && (
        <div className="rounded-md bg-pos/10 p-3 text-body">
          <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-pos" /> First address for {stage.path} — compare on the device:</p>
          <p className="mt-1 break-all font-mono">{stage.firstAddress}</p>
          <p className="mt-2 text-meta text-tg-hint">On the device: 0 TRX to this address — expired, never sent.</p>
          {/* words:ok — the device's own step, as ledger-cable words it */}
          <StateLine className="mt-1">{stage.text}</StateLine>
        </div>
      )}
      {(stage.at === 'exported' || stage.at === 'registered') && (
        <div className="rounded-md bg-pos/10 p-3 text-body">
          <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-pos" /> First address for {stage.path} — compare on the device:</p>
          <p className="mt-1 break-all font-mono">{stage.firstAddress}</p>
          <StateLine tone="good" className="mt-1" more={`${registeredFor ? 'Its addresses come from this account; the first is above.' : 'The bot issues an address per deal from it.'}${register.data && registered.some((k) => k.id === register.data!.id) ? ' It was on file already; this browser now checks its addresses.' : ''}`}>
            {stage.at !== 'registered' ? 'Recording the key…' : registeredFor ? `Registered as the key for ${registeredFor}.` : 'Registered.'}
          </StateLine>
        </div>
      )}
      {stage.at === 'failed' && (
        <StateLine tone="bad" more={<>{stage.more}{stage.remedy ? <span className="block">{stage.remedy}</span> : null}</>}>{stage.line}</StateLine>
      )}

      <button
        className="btn-primary flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
        disabled={register.isPending}
        onClick={() => register.mutate()}
      >
        <Usb className="h-3.5 w-3.5" /> {register.isPending ? 'On the Ledger…' : stage.at === 'failed' ? 'Try again' : 'Register this Ledger'}
      </button>
      <MoreDetails label="How to register">
        <p className="text-meta text-tg-hint">Ledger plugged in and unlocked, TRON app open, Ledger Live closed. The device exports the account's public key — nothing that can spend — and shows the account's first address: compare it with the one here before you confirm on the device.</p>
        <p className="text-meta text-tg-hint">One account per job: account 1 issues the customer deposit addresses; Hot, Merchant Hot and Treasury each take their addresses from their own account.</p>
        {REPLACEMENT_ORDER.map((l) => <p key={l} className="text-meta text-tg-hint">{l}</p>)}
        <p className="text-meta text-tg-hint">Accounts 2–4 also sign, on the device, a 0 TRX transfer from your Ledger to the new key's first address — shown in full, and never sendable: it expired before it was made. Compare the address on the device with the one it showed for the export, then approve.</p>
      </MoreDetails>
    </div>
  );
}

export function Keys() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['custody', 'me'], queryFn: api.me });
  const keys = useQuery({ queryKey: ['custody', 'keys'], queryFn: api.keys });
  // What each float holds, for the Retire confirm's procedure (the same key the Balances screen reads).
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, staleTime: 30_000, retry: false });
  const [registering, setRegistering] = useState(false);
  // One retirement confirmation on the screen at a time, closed while the Register form is open: one gold button.
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  return (
    <div className="space-y-3 lg:space-y-4">
      <Card title="Your signing device">
        {me.isError ? (
          isCustodySignin(me.error) ? <SigninQuiet error={me.error} /> : <StateLine tone="bad" more={textOf(me.error)}>Couldn't read your device — try again.</StateLine>
        ) : !me.data ? (
          <Pending inline what="Reading…" />
        ) : me.data.device ? (
          <div className="row">
            <span className="label">Ledger · {me.data.chain}</span>
            <Address value={me.data.device} />
          </div>
        ) : (
          <StateLine tone="warn" more="No signature of yours is taken until a device is registered. Ask for your Ledger's address to be registered by the desk.">
            No signing device registered for you.
          </StateLine>
        )}
        <MoreDetails label="What this is">
          <p className="text-meta text-tg-hint">The one key the desk takes a signature from you with. Registered by the desk, not from here.</p>
        </MoreDetails>
      </Card>

      <Card
        title="Account keys"
        action={!registering ? <button className="btn-secondary min-h-[44px] text-body lg:min-h-[40px]" onClick={() => { setConfirmingId(null); setRegistering(true); }}>Register this Ledger</button> : undefined}
      >
        {registering && (
          <div className="mb-3 rounded-md border border-stroke p-3">
            <RegisterKey registered={keys.data?.keys ?? []} device={me.data?.device ?? null} onRegistered={() => { void qc.invalidateQueries({ queryKey: ['custody', 'keys'] }); }} />
            <button className="btn-secondary mt-2 min-h-[44px] w-full text-body" onClick={() => setRegistering(false)}>Close</button>
          </div>
        )}
        {keys.isError ? (
          isCustodySignin(keys.error) ? <SigninQuiet error={keys.error} /> : <StateLine tone="bad" more={textOf(keys.error)}>Couldn't read the keys — try again.</StateLine>
        ) : !keys.data ? (
          <Pending inline what="Reading…" />
        ) : keys.data.keys.length === 0 ? (
          <StateLine tone="warn" more="Deposit addresses cannot be issued until account 1's key is registered.">No account key registered yet.</StateLine>
        ) : (
          keys.data.keys.map((k) => <KeyRow key={k.id} k={k} floats={balances.data?.wallets ?? null} confirming={confirmingId === k.id && !registering} setConfirming={(open) => setConfirmingId(open ? k.id : null)} onRetired={() => { for (const key of ['keys', 'renewals', 'balances']) void qc.invalidateQueries({ queryKey: ['custody', key] }); }} />)
        )}
        <MoreDetails label="What these are">
          <p className="text-meta text-tg-hint">One Ledger account per job, registered once: account 1 issues a deposit address per deal; Hot, Merchant Hot and Treasury take their addresses from accounts 2, 3 and 4. Nothing here can spend.</p>
        </MoreDetails>
      </Card>
    </div>
  );
}
